/**
 * Converts the markdown dialect WhatsApp sends into Atlassian (CommonMark)
 * markdown (BEARS-2293). Inverse of `toWhatsAppMarkdown`.
 *
 * | Format        | WhatsApp             | Atlassian    |
 * | ------------- | -------------------- | ------------ |
 * | Bold          | `*text*`             | `**text**`   |
 * | Strikethrough | `~text~`             | `~~text~~`   |
 * | Monospace     | ```` ```text``` ```` | `` `text` `` |
 * | Italic        | `_text_`             | `_text_`     |
 *
 * Italic already matches, so only bold, strikethrough, and monospace are
 * remapped. The WhatsApp→Atlassian direction is inherently ambiguous (a stray
 * `*`/`~` in prose could be misread as a delimiter), so conversion is
 * deliberately conservative: only well-formed, paired delimiters that hug
 * non-whitespace content are converted; already-doubled delimiters (`**`, `~~`)
 * are ignored so re-running is idempotent.
 *
 * This is a pure string transform with no feature-gating — callers decide when
 * to apply it (e.g. only for WhatsApp conversations, behind their own gate).
 */

// Monospace: exactly three backticks around content that hugs the delimiter,
// with no backticks inside. ```text``` → `text`.
const MONOSPACE = /```(?!`)(\S(?:[^`\n]*?\S)?)```(?!`)/g;
// Bold: a single `*` not part of `**`, hugging non-space content with no `*`.
const BOLD = /(?<!\*)\*(?!\*)(\S(?:[^*\n]*?\S)?)\*(?!\*)/g;
// Strikethrough: a single `~` not part of `~~`, hugging non-space content with
// no `~`.
const STRIKETHROUGH = /(?<!~)~(?!~)(\S(?:[^~\n]*?\S)?)~(?!~)/g;
// Monospace runs: their contents are literal, so bold/strike must not touch them.
const MONOSPACE_SPAN = /```(?!`)\S(?:[^`\n]*?\S)?```(?!`)/g;

// Private-use sentinel that won't appear in real message text, used to mask
// monospace spans while marks are converted around them.
const SPAN_SENTINEL = '\uE000';

// Runs `transform` on the text outside monospace spans, leaving span contents
// literal.
const outsideMonospace = (input: string, transform: (chunk: string) => string): string => {
	const spans: string[] = [];
	const masked = input.replace(MONOSPACE_SPAN, (span) => {
		spans.push(span);
		return `${SPAN_SENTINEL}${spans.length - 1}${SPAN_SENTINEL}`;
	});
	return transform(masked).replace(
		new RegExp(`${SPAN_SENTINEL}(\\d+)${SPAN_SENTINEL}`, 'g'),
		(_m, i) => spans[Number(i)],
	);
};

export const toAtlassianMarkdown = (markdown: string): string => {
	if (!markdown) {
		return markdown;
	}

	return outsideMonospace(markdown, (chunk) =>
		chunk.replace(BOLD, '**$1**').replace(STRIKETHROUGH, '~~$1~~'),
	).replace(MONOSPACE, '`$1`');
};
